/**
 * MobileCommunityClient — Lightweight standalone mobile community page.
 * Replicates CommunityClient mobile-mode UI without the heavy desktop overhead.
 * Uses CSS variables for theming, direct API calls for data.
 *
 * NO: AnimationLayer, useScale, useDynamicBackground, useSwipe, MainNavbar,
 *     PixelatedTurtle, CommunityFrameDesign, localTheme
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Heart, MessageSquare, Share2, ThumbsDown, ChevronDown, TrendingUp, TrendingDown,
  Flame, Calendar, Clock, Repeat2, Users, Pin, Send, Palette,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCommunityPostUrl, getForumPostUrl } from '../../utils/postUrls';
import { PostBadges } from '../desktop/PostBadges';
import { selectPreviewComments } from '../../utils/commentPreview';
import resolveAvatar from '../../lib/resolveAvatar';
import { MobilePostOverlay } from './MobilePostOverlay';
import { MobileCommunityThemeCustomizer } from './MobileThemeBottomSheet';
import { SubPanelSwiper } from './Navigation';
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { getBackground, getCardBackground, hexToRgba } from '../../utils/backgroundHelpers';
import { getThemeByKey, type PresetThemeKey, PRESET_THEMES } from '@theme/tokens';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const mins = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Theme helpers (same logic as MobileCommunityOverlay / MobileProfile)
// ---------------------------------------------------------------------------

function buildCommunityThemeVars(raw: any): React.CSSProperties {
  if (!raw) return {};
  // Always merge with preset defaults (matching desktop CommunityClient behavior).
  // Desktop uses: { ...defaultCommunityCustomization, ...community.communityCustomization }
  // where defaultCommunityCustomization = { ...PRESET_THEMES['ocean-turtle'], ... }
  const presetKey = (raw.baseTheme && raw.baseTheme in PRESET_THEMES)
    ? raw.baseTheme as PresetThemeKey
    : 'ocean-turtle' as PresetThemeKey;
  const preset = getThemeByKey(presetKey);
  let c = { ...preset, ...raw };
  // Preserve preset card gradient if saved data doesn't override it
  if (preset.cardGradientEnabled && !raw.hasOwnProperty?.('cardGradientEnabled')) {
    c.cardGradientEnabled = true;
  }
  if (preset.cardGradientColor1 && !c.cardGradientColor1?.startsWith('#')) {
    c.cardGradientColor1 = preset.cardGradientColor1;
  }
  if (preset.cardGradientColor2 && !c.cardGradientColor2?.startsWith('#')) {
    c.cardGradientColor2 = preset.cardGradientColor2;
  }
  if (!c.primaryColor) return {};

  const colorSpace = c.colorSpace || 'Off';

  const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
    if (!hex || !hex.startsWith('#') || hex.length < 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  let textColor = c.textColor;
  let accentColor = c.accentColor;
  let primaryColor = c.primaryColor;
  let secondaryColor = c.secondaryColor;
  let cardBg = c.cardBg;

  if (colorSpace !== 'Off') {
    const opts: ColorTransformOptions = {
      colorSpace,
      temperatureLimit: c.temperatureLimit ?? 50,
      magentaLimit: c.magentaLimit ?? 50,
      intensityLimit: c.intensityLimit ?? 50,
      saturationLimit: c.backgroundSaturationLimit ?? 50,
      brightnessLimit: c.backgroundBrightnessLimit ?? 50,
      redLimit: c.redLimit ?? 50,
      greenLimit: c.greenLimit ?? 50,
      blueLimit: c.blueLimit ?? 50,
      contrast: c.backgroundContrast ?? 50,
      hueGravity: c.backgroundHueGravity ?? 0,
      graySaturation: c.backgroundGraySaturation ?? 0,
      targetHue: c.backgroundDefaultHue ?? 184,
    };
    textColor = transformColor(textColor, opts);
    accentColor = transformColor(accentColor, opts);
    primaryColor = transformColor(primaryColor, opts);
    secondaryColor = transformColor(secondaryColor, opts);
    if (cardBg?.startsWith('#')) cardBg = transformColor(cardBg, opts);
  }

  const navbarBg = hexToRgbaWithOpacity(
    c.navbarBg?.startsWith('#') ? (colorSpace !== 'Off' ? transformColor(c.navbarBg, { colorSpace } as any) : c.navbarBg) : primaryColor,
    c.navbarOpacity ?? 92,
  );
  const tabBarBg = c.tabBarBg
    ? hexToRgbaWithOpacity(c.tabBarBg.startsWith('#') && colorSpace !== 'Off' ? transformColor(c.tabBarBg, { colorSpace } as any) : c.tabBarBg, c.tabBarOpacity ?? 88)
    : navbarBg;

  const bg = getBackground(
    { primaryColor, secondaryColor, cardBg: cardBg || primaryColor },
    { gradientEnabled: c.gradientEnabled, gradientAngle: c.gradientAngle, gradientFavorability: c.gradientFavorability, gradientStrength: c.gradientStrength },
    primaryColor,
  );

  return {
    '--text-color': textColor,
    '--color-accent': accentColor,
    '--primary-color': primaryColor,
    '--secondary-color': secondaryColor,
    '--card-bg': getCardBackground(
      { primaryColor, secondaryColor, cardBg: cardBg || primaryColor, cardGradientColor1: c.cardGradientColor1, cardGradientColor2: c.cardGradientColor2 },
      { cardGradientEnabled: c.cardGradientEnabled, cardGradientAngle: c.cardGradientAngle, cardGradientFavorability: c.cardGradientFavorability, cardGradientStrength: c.cardGradientStrength, cardOpacity: c.cardOpacity },
      primaryColor,
    ),
    '--navbar-bg': navbarBg,
    '--tab-bar-bg': tabBarBg,
    '--app-bg': bg,
  } as React.CSSProperties;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'public' as const, label: 'Public' },
  { key: 'members' as const, label: 'Members' },
  { key: 'staff' as const, label: 'Staff' },
  { key: 'forum' as const, label: 'Forum' },
  { key: 'chat' as const, label: 'Chat' },
];

type CommunityTab = typeof TABS[number]['key'];

const SORT_OPTIONS = [
  { value: 'newest' as const, label: 'Newest', icon: Calendar },
  { value: 'oldest' as const, label: 'Oldest', icon: Clock },
  { value: 'popular' as const, label: 'Popular', icon: TrendingUp },
];

type SortValue = typeof SORT_OPTIONS[number]['value'];


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommunityData {
  id: string;
  name: string;
  nameSlug: string;
  description: string;
  avatar?: string;
  memberCount: number;
  postCount: number;
  threadCount: number;
  currentUserRole?: string;
}

// ---------------------------------------------------------------------------
// MobileCommunityClient
// ---------------------------------------------------------------------------

export interface MobileCommunityClientProps {
  slug: string;
  activePanel: number;
  onPanelChange: (index: number) => void;
  screenWidth: number;
}

export function MobileCommunityClient({ slug, activePanel, onPanelChange, screenWidth }: MobileCommunityClientProps) {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  // Community data
  const [community, setCommunity] = useState<CommunityData | null>(null);
  const [communityCustomization, setCommunityCustomization] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Posts
  const [posts, setPosts] = useState<any[]>([]);
  const [forumPosts, setForumPosts] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<any[]>([]);

  // UI state
  const [activeTab, setActiveTab] = useState<CommunityTab>('public');
  const [sortBy, setSortBy] = useState<SortValue>('newest');
  const [isFollowing, setIsFollowing] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [localMemberCount, setLocalMemberCount] = useState(0);
  const [showPostModal, setShowPostModal] = useState(false);
  const [isShareAnimating, setIsShareAnimating] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);

  // Chat
  const [chatMessage, setChatMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentSortOption = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];

  // Compute community theme CSS vars from the community's customization
  const communityThemeVars = useMemo(
    () => buildCommunityThemeVars(communityCustomization),
    [communityCustomization]
  );

  // Build current user for modals
  const currentUser = authUser ? {
    id: authUser.id.toString(),
    username: authUser.username,
    displayName: authUser.name || authUser.username,
    avatar: authUser.profilePicture || undefined,
    isSiteAdmin: authUser.isSiteAdmin || false,
    isSuperAdmin: authUser.isSuperAdmin || false,
    isSiteModerator: authUser.isSiteModerator || false,
  } : null;

  // ── Fetch community data ──
  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    setError(false);

    (async () => {
      try {
        const res = await fetchWithAuth(`/api/communities/slug/${slug}`, { credentials: 'include' });
        if (!res.ok) { setError(true); setIsLoading(false); return; }
        const data = await res.json();
        const comm = data.community || data;

        const communityData: CommunityData = {
          id: comm.id,
          name: comm.name,
          nameSlug: comm.nameSlug || slug,
          description: comm.description || '',
          avatar: comm.avatar,
          memberCount: comm.memberCount || comm._count?.members || 0,
          postCount: comm.postCount || comm._count?.posts || 0,
          threadCount: comm.threadCount || comm._count?.threads || 0,
          currentUserRole: comm.currentUserRole,
        };

        setCommunity(communityData);
        setLocalMemberCount(communityData.memberCount);
        setIsFollowing(!!comm.currentUserRole);
        if (comm.communityCustomization) {
          setCommunityCustomization(comm.communityCustomization);
        }
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [slug]);

  // Listen for community theme updates (from MobileCommunityThemeCustomizer)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.customization) setCommunityCustomization(detail.customization);
    };
    window.addEventListener('communityThemeUpdated', handler);
    return () => window.removeEventListener('communityThemeUpdated', handler);
  }, []);

  // ── Fetch posts for current tab ──
  useEffect(() => {
    if (!community) return;

    (async () => {
      try {
        if (activeTab === 'public' || activeTab === 'members' || activeTab === 'staff') {
          const res = await fetchWithAuth(`/api/communities/${community.id}/posts?tab=${activeTab}`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setPosts(data.posts || []);
          }
        } else if (activeTab === 'forum') {
          const res = await fetchWithAuth(`/api/communities/${community.id}/posts?postType=forum`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setForumPosts(data.posts || []);
          }
        } else if (activeTab === 'chat') {
          const res = await fetchWithAuth(`/api/communities/${community.id}/chat`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setChatMessages(data.messages || data || []);
          }
        }
      } catch { /* silent */ }
    })();
  }, [community, activeTab]);

  // Auto-scroll chat
  useEffect(() => {
    if (activeTab === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  // ── Join / Leave ──
  const handleJoinLeave = useCallback(async () => {
    if (!community || isJoining) return;
    setIsJoining(true);
    try {
      if (isFollowing) {
        const res = await fetchWithAuth(`/api/communities/${community.id}/leave`, { method: 'POST', credentials: 'include' });
        if (res.ok) {
          setIsFollowing(false);
          setLocalMemberCount(prev => Math.max(0, prev - 1));
        }
      } else {
        const { joinCommunity } = await import('../../lib/api-client');
        await joinCommunity(String(community.id), {});
        setIsFollowing(true);
        setLocalMemberCount(prev => prev + 1);
      }
    } catch (err) {
      if (!isFollowing) alert(err instanceof Error ? err.message : 'Failed to join');
    } finally {
      setIsJoining(false);
    }
  }, [community, isFollowing, isJoining]);

  // ── Like / Dislike (optimistic) ──
  const handleLike = useCallback(async (postId: string) => {
    if (!community) return;
    const target = posts.find(p => p.id === postId) || forumPosts.find(p => p.id === postId);
    if (!target) return;

    const wasLiked = target.liked || false;
    const initialLikes = target.likes || 0;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    const update = (list: any[]) => list.map((p: any) =>
      p.id === postId ? { ...p, liked: newLiked, likes: newLikes, disliked: false } : p
    );
    setPosts(update);
    setForumPosts(update);

    try {
      await fetchWithAuth(`/api/communities/${community.id}/posts/${postId}/like`, { method: 'POST', credentials: 'include' });
    } catch {
      const revert = (list: any[]) => list.map((p: any) =>
        p.id === postId ? { ...p, liked: wasLiked, likes: initialLikes } : p
      );
      setPosts(revert);
      setForumPosts(revert);
    }
  }, [community, posts, forumPosts]);

  const handleDislike = useCallback(async (postId: string) => {
    if (!community) return;
    const target = posts.find(p => p.id === postId) || forumPosts.find(p => p.id === postId);
    if (!target) return;

    const wasDisliked = target.disliked || false;
    const initialDislikes = target.dislikes || 0;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    const update = (list: any[]) => list.map((p: any) =>
      p.id === postId ? { ...p, disliked: newDisliked, dislikes: newDislikes, liked: false } : p
    );
    setPosts(update);
    setForumPosts(update);

    try {
      await fetchWithAuth(`/api/communities/${community.id}/posts/${postId}/dislike`, { method: 'POST', credentials: 'include' });
    } catch {
      const revert = (list: any[]) => list.map((p: any) =>
        p.id === postId ? { ...p, disliked: wasDisliked, dislikes: initialDislikes } : p
      );
      setPosts(revert);
      setForumPosts(revert);
    }
  }, [community, posts, forumPosts]);

  // ── Comment like/dislike ──
  const likeComment = useCallback(async (commentId: string, postId: string) => {
    if (!community) return;
    try {
      await fetchWithAuth(`/api/communities/${community.id}/replies/${commentId}/like`, { method: 'POST', credentials: 'include' });
    } catch { /* silent */ }
  }, [community]);

  const dislikeComment = useCallback(async (commentId: string, postId: string) => {
    if (!community) return;
    try {
      await fetchWithAuth(`/api/communities/${community.id}/replies/${commentId}/dislike`, { method: 'POST', credentials: 'include' });
    } catch { /* silent */ }
  }, [community]);

  // ── Create post ──
  const handleCreatePost = useCallback(async (postData: {
    title: string; content: string; tags: string[]; mediaUrl: string; mediaPosition: 'above' | 'below';
    communityId?: string; postType?: 'community' | 'forum'; visibility?: 'public' | 'members-only';
  }) => {
    if (!community) return;
    try {
      const res = await fetchWithAuth(`/api/communities/${community.id}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: postData.title.trim() || undefined,
          content: postData.content,
          tags: postData.tags,
          postType: postData.postType || 'community',
          mediaUrl: postData.mediaUrl || undefined,
          mediaPosition: postData.mediaPosition,
          visibility: postData.visibility === 'members-only' ? 'private' : 'public',
        }),
      });
      if (res.ok) {
        // Re-fetch posts for current tab
        const postsRes = await fetchWithAuth(`/api/communities/${community.id}/posts?tab=${activeTab}`, { credentials: 'include' });
        if (postsRes.ok) {
          const data = await postsRes.json();
          setPosts(data.posts || []);
        }
        setShowPostModal(false);
      }
    } catch { /* silent */ }
  }, [community, activeTab]);

  // ── Send chat message ──
  const handleSendChat = useCallback(async () => {
    if (!chatMessage.trim() || !community || isSendingMessage) return;
    setIsSendingMessage(true);
    const msg = chatMessage;
    setChatMessage('');

    try {
      const res = await fetchWithAuth(`/api/communities/${community.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: msg }),
      });
      if (res.ok) {
        // Re-fetch chat messages
        const chatRes = await fetchWithAuth(`/api/communities/${community.id}/chat`, { credentials: 'include' });
        if (chatRes.ok) {
          const data = await chatRes.json();
          setChatMessages(data.messages || data || []);
        }
      } else {
        setChatMessage(msg);
      }
    } catch {
      setChatMessage(msg);
    } finally {
      setIsSendingMessage(false);
    }
  }, [chatMessage, community, isSendingMessage]);

  // Share click
  const handleShareClick = useCallback(() => {
    setIsShareAnimating(true);
    setTimeout(() => {
      setShowPostModal(true);
      setIsShareAnimating(false);
    }, 300);
  }, []);

  // Sort posts
  const sortedPosts = [...posts].sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'popular': return (b.likes || 0) - (a.likes || 0);
      default: return 0;
    }
  });

  const sortedForumPosts = [...forumPosts].sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'popular': return (b.likes || 0) - (a.likes || 0);
      default: return 0;
    }
  });

  // ── Loading / Error states ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error || !community) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>Community not found</span>
        <button onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-xl text-sm font-medium"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="h-full" style={{ ...communityThemeVars, background: communityThemeVars['--app-bg' as keyof typeof communityThemeVars] || undefined }}>
    <SubPanelSwiper activePanel={activePanel} onPanelChange={onPanelChange} screenWidth={screenWidth}>
      {[
        /* ── Left Panel: Post Creation ── */
        <div key="left" className="pt-6 px-4 h-full overflow-auto flex flex-col items-stretch gap-3"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top) + 1rem)' }}>
          {currentUser && (
            <>
              <button
                onClick={handleShareClick}
                className="rounded-lg shadow-lg font-semibold overflow-hidden w-full"
                style={{
                  backgroundColor: 'var(--color-accent)',
                  color: 'var(--text-color)',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  minHeight: '48px',
                }}
              >
                <span style={{
                  display: 'inline-block',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: isShareAnimating ? 0 : 1,
                  transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
                }}>
                  {showPostModal ? 'Post' : 'Share'}
                </span>
              </button>
              <p className="text-xs text-center" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
                Tap to create a post
              </p>
            </>
          )}

        </div>,

        /* ── Center Panel: Community Info + Content ── */
        <div key="center" className="flex flex-col items-center w-full px-2 h-full overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}>

          {/* Community Card */}
          <div className="rounded-2xl w-full" style={{ background: 'var(--card-bg)', padding: '16px', marginBottom: '24px' }}>
            <div className="flex flex-col gap-4">
              {/* Avatar */}
              <div className="relative">
                <div className="w-24 h-24 relative overflow-hidden rounded-full" style={{ border: '4px solid var(--color-accent)' }}>
                  {community.avatar ? (
                    <img src={resolveAvatar(community.avatar)} alt={community.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.3)' }}>
                      <Users size={40} style={{ color: 'var(--text-color)' }} />
                    </div>
                  )}
                </div>
                <p className="text-sm font-medium mt-2" style={{ color: 'var(--text-color)' }}>
                  {localMemberCount.toLocaleString()} members
                </p>
              </div>

              {/* Name + Join */}
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-3">
                  <h1 className="text-2xl font-bold" style={{ color: 'var(--text-color)' }}>
                    {community.name}
                  </h1>
                  <button
                    onClick={handleJoinLeave}
                    disabled={isJoining}
                    className="rounded-lg font-semibold"
                    style={{
                      padding: '8px 20px',
                      backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : 'var(--color-accent)',
                      color: 'var(--text-color)',
                      border: isFollowing ? '1px solid var(--color-accent)' : '1px solid transparent',
                    }}
                  >
                    {isJoining ? 'Loading...' : isFollowing ? 'Joined' : 'Join'}
                  </button>
                </div>

                {/* Description */}
                {community.description && (
                  <div className="flex flex-col gap-2">
                    {community.description.split('\n').filter((l: string) => l.trim()).map((line: string, i: number) => (
                      <p key={i} className="text-base italic opacity-85" style={{ color: 'var(--text-color)' }}>
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="w-full mb-4">
            <div className="rounded-lg py-3 px-4 flex items-center" style={{ background: 'var(--tab-bar-bg, rgba(30, 58, 95, 0.95))' }}>
              <div className="flex items-center overflow-x-auto flex-1 gap-3" style={{ WebkitOverflowScrolling: 'touch' }}>
                {TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className="text-sm font-semibold pb-1 border-b-2 whitespace-nowrap"
                    style={{
                      color: activeTab === key ? 'var(--text-color)' : 'var(--text-color-muted, rgba(255,255,255,0.6))',
                      borderColor: activeTab === key ? 'var(--color-accent)' : 'transparent',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Sort dropdown */}
              <div className="relative shrink-0 ml-2" style={{ zIndex: 41 }}>
                <button
                  onClick={() => setShowSortMenu(prev => !prev)}
                  className="flex items-center rounded-lg font-medium"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-color)',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                  }}
                >
                  <currentSortOption.icon size={14} />
                  <ChevronDown size={14} className={showSortMenu ? 'rotate-180' : ''} style={{ transition: 'transform 0.2s' }} />
                </button>
                {showSortMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                    <div className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl z-50" style={{
                      background: 'var(--card-bg)',
                      border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)',
                      marginTop: '4px',
                      minWidth: '140px',
                    }}>
                      {SORT_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          onClick={() => { setSortBy(option.value); setShowSortMenu(false); }}
                          className="flex items-center gap-2 w-full text-left"
                          style={{
                            padding: '10px 14px',
                            backgroundColor: sortBy === option.value ? 'rgba(var(--accent-rgb, 6,182,212), 0.2)' : 'transparent',
                            color: 'var(--text-color)',
                            fontSize: '13px',
                          }}
                        >
                          <option.icon size={14} style={{ opacity: 0.7 }} />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Wavy divider */}
          <div className="w-full" style={{ marginBottom: '16px' }}>
            <svg width="100%" height="12" viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path
                d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
                fill="none" stroke="var(--color-accent)" strokeWidth="2" opacity="0.4"
              />
            </svg>
          </div>

          {/* ── Posts (public/members/staff) ── */}
          {(activeTab === 'public' || activeTab === 'members' || activeTab === 'staff') && (
            <div className="w-full flex flex-col gap-4">
              {sortedPosts.length === 0 ? (
                <div className="rounded-xl p-6" style={{ background: 'var(--card-bg)' }}>
                  <p className="text-center opacity-60" style={{ color: 'var(--text-color)' }}>No posts yet</p>
                </div>
              ) : sortedPosts.map((post: any) => (
                <CommunityPostCard
                  key={post.id}
                  post={post}
                  communitySlug={community.nameSlug}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  onLikeComment={likeComment}
                  onDislikeComment={dislikeComment}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {/* ── Forum Tab ── */}
          {activeTab === 'forum' && (
            <div className="w-full flex flex-col gap-4">
              {sortedForumPosts.length === 0 ? (
                <div className="rounded-xl p-6" style={{ background: 'var(--card-bg)' }}>
                  <p className="text-center opacity-60" style={{ color: 'var(--text-color)' }}>No forum threads yet</p>
                </div>
              ) : sortedForumPosts.map((post: any) => (
                <CommunityPostCard
                  key={post.id}
                  post={post}
                  communitySlug={community.nameSlug}
                  isForum
                  onLike={handleLike}
                  onDislike={handleDislike}
                  onLikeComment={likeComment}
                  onDislikeComment={dislikeComment}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {/* ── Chat Tab ── */}
          {activeTab === 'chat' && (
            <div className="w-full rounded-2xl border flex flex-col" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              height: '500px',
            }}>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ WebkitOverflowScrolling: 'touch' }}>
                {chatMessages.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.4 }}>No messages yet</span>
                  </div>
                )}
                {chatMessages.slice(-100).map((msg: any) => (
                  <div key={msg.id} className="flex gap-2">
                    <Link to={`/user/${msg.author?.username}`} className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                      style={{ border: '2px solid var(--color-accent)' }}>
                      {msg.author?.avatar ? (
                        <img src={resolveAvatar(msg.author.avatar)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs font-bold"
                          style={{ background: 'var(--color-accent)', color: 'var(--app-bg)' }}>
                          {(msg.author?.displayName || msg.author?.username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </Link>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-xs" style={{ color: 'var(--text-color)' }}>
                          {msg.author?.displayName || msg.author?.username || 'Unknown'}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.5 }}>
                          {formatTimeAgo(msg.createdAt)}
                        </span>
                      </div>
                      {msg.deletedAt ? (
                        <div className="text-xs italic px-2 py-1 rounded border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.2)', color: 'var(--text-color)', opacity: 0.5 }}>
                          Message deleted
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.9 }}>{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              {currentUser && (
                <div className="border-t p-3 flex items-center gap-2" style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)' }}>
                  <input
                    type="text"
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                    placeholder="Type a message..."
                    disabled={isSendingMessage}
                    className="flex-1 rounded-lg outline-none text-sm"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'var(--text-color)',
                      border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)',
                      padding: '0.5rem 0.75rem',
                    }}
                  />
                  <button
                    onClick={handleSendChat}
                    disabled={!chatMessage.trim() || isSendingMessage}
                    className="rounded-lg disabled:opacity-50 p-2"
                    style={{ backgroundColor: 'var(--color-accent)', color: 'var(--text-color)' }}
                  >
                    <Send size={18} />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="h-24 shrink-0" />
        </div>,

        /* ── Right Panel: Community Info + Sort ── */
        <div key="right" className="pt-6 px-4 h-full overflow-auto flex flex-col gap-4">
          {/* Community Info Stats */}
          <div className="rounded-xl p-4" style={{ background: 'var(--card-bg)' }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-color)' }}>
              Community Info
            </h3>
            <div className="space-y-2 text-xs" style={{ color: 'var(--text-color)', opacity: 0.8 }}>
              <p>{localMemberCount.toLocaleString()} members</p>
              <p>{community.postCount || 0} posts</p>
              <p>{community.threadCount || 0} threads</p>
            </div>
          </div>

          {/* Customize Theme (admin/owner only) */}
          {community && (community.currentUserRole === 'owner' || community.currentUserRole === 'admin') && (
            <button
              onClick={() => setShowThemeCustomizer(true)}
              className="w-full rounded-xl p-4 flex items-center gap-3"
              style={{ background: 'var(--card-bg)' }}
            >
              <Palette size={18} style={{ color: 'var(--color-accent)' }} />
              <span className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
                Customize Theme
              </span>
            </button>
          )}
        </div>,
      ]}
    </SubPanelSwiper>

    <MobilePostOverlay
      isOpen={showPostModal}
      onClose={() => setShowPostModal(false)}
      onSubmit={handleCreatePost}
      currentUser={currentUser}
      heading={`Post to ${community.name}`}
      communities={[{ id: String(community.id), name: community.name }]}
      selectedCommunityId={String(community.id)}
      hideCommunitySelector={true}
      showPostTypeSelector={true}
      showVisibilitySelector={true}
    />

    {community && (
      <MobileCommunityThemeCustomizer
        isOpen={showThemeCustomizer}
        onClose={() => setShowThemeCustomizer(false)}
        communityId={String(community.id)}
      />
    )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Community Post Card
// ---------------------------------------------------------------------------

function CommunityPostCard({
  post, communitySlug, isForum, onLike, onDislike, onLikeComment, onDislikeComment, navigate,
}: {
  post: any;
  communitySlug: string;
  isForum?: boolean;
  onLike: (id: string) => void;
  onDislike: (id: string) => void;
  onLikeComment: (commentId: string, postId: string) => void;
  onDislikeComment: (commentId: string, postId: string) => void;
  navigate: (path: string) => void;
}) {
  const isForumPost = isForum || post.postType === 'forum';
  const postUrl = isForumPost
    ? getForumPostUrl(communitySlug, post.slug || '')
    : getCommunityPostUrl(communitySlug, post.slug || '');
  const navState = { state: { themeContext: { type: 'community' as const } } };

  return (
    <div className="rounded-2xl border relative" style={{
      background: isForumPost ? 'rgba(255, 255, 255, 0.06)' : 'var(--card-bg)',
      borderColor: isForumPost ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)',
      padding: '1rem',
    }}>
      <PostBadges post={post as any} textColor="var(--text-color)" accentColor="var(--color-accent)" />

      {/* Author row */}
      <div className="flex items-start gap-3 mb-3">
        <Link
          to={post.author?.username ? `/user/${post.author.username}` : '#'}
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--color-accent)' }}
        >
          {post.author?.avatar ? (
            <img src={resolveAvatar(post.author.avatar)} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-sm font-bold" style={{ color: 'var(--app-bg)' }}>
              {(post.author?.displayName || post.author?.username || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          {/* Forum title */}
          {isForumPost && post.title && (
            <Link to={postUrl} {...navState} className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>{post.title}</h2>
              {post.pinned && <Pin size={14} style={{ color: 'var(--color-accent)' }} />}
            </Link>
          )}
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm" style={{ color: 'var(--text-color)' }}>
              {post.author?.displayName || post.author?.username || 'Unknown'}
            </h3>
            <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.5 }}>
              {formatTimeAgo(post.createdAt)}
            </span>
          </div>

          {/* Non-forum title + content inline */}
          {!isForumPost && (
            <Link to={postUrl} {...navState} className="block mt-2">
              {post.title && (
                <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-color)' }}>{post.title}</h2>
              )}
              <p className="leading-relaxed text-sm" style={{ color: 'var(--text-color)', opacity: 0.9 }}>
                {post.content}
              </p>
            </Link>
          )}
        </div>
      </div>

      {/* Forum content */}
      {isForumPost && (
        <Link to={postUrl} {...navState} className="block">
          <p className="leading-relaxed text-sm mb-3" style={{ color: 'var(--text-color)', opacity: 0.9 }}>{post.content}</p>
        </Link>
      )}

      {/* Media */}
      {post.mediaUrl && (
        <div className="my-2 rounded-lg overflow-hidden">
          <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-4 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <button onClick={() => onLike(post.id)} className="flex items-center gap-1"
          style={{ color: post.liked ? '#ef4444' : 'var(--text-color)', opacity: post.liked ? 1 : 0.6, minHeight: '44px' }}>
          <Heart size={16} fill={post.liked ? '#ef4444' : 'none'} />
          <span className="text-xs">{post.likes || 0}</span>
        </button>
        <button onClick={() => onDislike(post.id)} className="flex items-center gap-1"
          style={{ color: post.disliked ? '#f97316' : 'var(--text-color)', opacity: post.disliked ? 1 : 0.6, minHeight: '44px' }}>
          <ThumbsDown size={16} fill={post.disliked ? '#f97316' : 'none'} />
          <span className="text-xs">{post.dislikes || 0}</span>
        </button>
        <button onClick={() => navigate(postUrl)} className="flex items-center gap-1"
          style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
          <MessageSquare size={16} />
          <span className="text-xs">{post._count?.replies || post.comments || 0}</span>
        </button>
        <button className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
          <Repeat2 size={16} />
        </button>
        <button className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
          <Share2 size={16} />
        </button>
      </div>

      {/* Comment Previews */}
      {post.replies && post.replies.length > 0 && (() => {
        const previewComments = selectPreviewComments(post.replies);
        return (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.12)' }}>
            <div className="flex flex-col gap-2.5">
              {previewComments.map((reply: any) => (
                <div key={reply.id} className="flex gap-2">
                  <Link to={`/user/${reply.author?.username}`}
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--color-accent)' }}>
                    {reply.author?.profilePicture ? (
                      <img src={resolveAvatar(reply.author.profilePicture)} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold" style={{ color: 'var(--app-bg)' }}>
                        {(reply.author?.profileName || reply.author?.username || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-semibold truncate" style={{ color: 'var(--text-color)' }}>
                        {reply.author?.profileName || reply.author?.username}
                      </span>
                      <span className="text-xs shrink-0" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
                        {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-xs line-clamp-2" style={{ color: 'var(--text-color)', opacity: 0.8 }}>{reply.content}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <button onClick={(e) => { e.stopPropagation(); onLikeComment(reply.id, post.id); }}
                        className="flex items-center gap-1"
                        style={{ color: reply.liked ? '#ef4444' : 'var(--text-color)', opacity: reply.liked ? 1 : 0.4 }}>
                        <Heart size={11} fill={reply.liked ? '#ef4444' : 'none'} />
                        <span className="text-xs">{reply.likes}</span>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onDislikeComment(reply.id, post.id); }}
                        className="flex items-center gap-1"
                        style={{ color: reply.disliked ? '#f97316' : 'var(--text-color)', opacity: reply.disliked ? 1 : 0.4 }}>
                        <ThumbsDown size={11} fill={reply.disliked ? '#f97316' : 'none'} />
                        <span className="text-xs">{reply.dislikes}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {(post._count?.replies || post.comments || 0) > 3 && (
              <button onClick={() => navigate(postUrl)} className="text-xs font-semibold mt-2 block"
                style={{ color: 'var(--color-accent)' }}>
                View all {post._count?.replies || post.comments} comments
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}
